import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { JjamesButtonComponent, JJamesPaginationConfig, JJamesPaginatorComponent } from 'shared-lib';
import {
    EnrichedPaymentObligationDto,
    ObligationStatus,
    ParkingSpaceDto,
    ParkingSpaceService,
    PaymentObligationDto,
    PaymentObligationService,
    PaymentObligationStatsDto,
    PaymentRevenueStatsDto,
    PaymentService,
    SortOrder
} from '../../api/gen';
import {
    ObligationActionDialogComponent,
    ObligationAction,
    ObligationActionDialogData
} from './obligation-action-dialog/obligation-action-dialog.component';
import {
    ExportScope,
    ExportScopeDialogComponent,
    ExportScopeDialogData
} from './export-scope-dialog/export-scope-dialog.component';
import { PaymentsListComponent } from './payments-list/payments-list.component';
import { AllPaymentsListComponent } from './all-payments-list/all-payments-list.component';
import { ReportsAnalyticsComponent } from './reports-analytics/reports-analytics.component';
import { CoinMachinesComponent } from './coin-machines/coin-machines.component';

export type ObligationsTab = 'overview' | 'revenue' | 'obligations' | 'all-payments' | 'reports' | 'coin-machines';

@Component({
    selector: 'app-payment-obligations',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        JjamesButtonComponent,
        JJamesPaginatorComponent,
        PaymentsListComponent,
        AllPaymentsListComponent,
        ReportsAnalyticsComponent,
        CoinMachinesComponent,
        TranslatePipe
    ],
    templateUrl: './payment-obligations.component.html',
    styleUrl: './payment-obligations.component.scss'
})
export class PaymentObligationsComponent implements OnInit {
    /**
     * Rows one export may contain.
     */
    private static readonly MaxExportRows = 20_000;
    private static readonly ExportPageSize = 200;

    private obligationService = inject(PaymentObligationService);
    private paymentService = inject(PaymentService);
    private parkingSpaceService = inject(ParkingSpaceService);
    private dialog = inject(MatDialog);
    private snackBar = inject(MatSnackBar);
    private translate = inject(TranslateService);

    activeTab = signal<ObligationsTab>('overview');
    obligations = signal<EnrichedPaymentObligationDto[]>([]);
    isLoading = signal(false);
    isExporting = signal(false);
    totalCount = signal(0);

    statusFilter = signal<ObligationStatus | ''>('');
    plateFilter = signal('');

    sites = signal<ParkingSpaceDto[]>([]);
    siteFilter = signal('');

    pageIndex = signal(0);
    pageSize = signal(20);

    stats = signal<PaymentObligationStatsDto | undefined>(undefined);
    revenueStats = signal<PaymentRevenueStatsDto | undefined>(undefined);
    statsFailed = signal(false);

    totalCollectedAmount = computed(() => this.stats()?.totalCollectedAmount ?? 0);
    outstandingAmount = computed(() => this.stats()?.outstandingAmount ?? 0);
    waivedCancelledAmount = computed(() => this.stats()?.waivedCancelledAmount ?? 0);
    expiredAmount = computed(() => this.stats()?.expiredAmount ?? 0);

    directNetRevenue = computed(() => this.revenueStats()?.netRevenue ?? 0);
    totalEstateRevenue = computed(() => this.directNetRevenue() + this.totalCollectedAmount());

    /**
     * Currency for the totals. Falls back to a valid code before the first load and when there is
     * nothing to total, because the server sends an empty string in that case and the currency pipe
     * needs something it can format.
     */
    readonly currencyCode = computed(() => {
        const code = this.stats()?.currency;
        return code && code.length > 0 ? code : 'EUR';
    });

    /** Adding euros to francs produces a number that means nothing, so the page says so instead. */
    readonly hasMixedCurrencies = computed(() => this.stats()?.hasMixedCurrencies === true);

    /** Until the totals have arrived the tiles show a dash, never a zero. */
    readonly statsLoaded = computed(() => this.stats() !== undefined);

    /** Counts cover every currency: "how many are under review" is a question about work, not money. */
    statusCounts = computed<Record<string, number>>(() => {
        const fromServer = this.stats()?.statusCounts ?? {};
        const counts: Record<string, number> = {};
        for (const status of this.statuses) {
            counts[status] = fromServer[status] ?? 0;
        }
        return counts;
    });

    /**
     * Collection rate, or a dash while there is still no answer to report.
     *
     * The rate is paid against everything that concluded — paid or expired. Waived and cancelled are
     * excluded on purpose: an operator writing a debt off is not a collection failure. With nothing
     * concluded there is no ratio at all, and the previous version answered 100% for an empty list,
     * which reads as perfect collection on a site that has collected nothing.
     */
    readonly collectionRateLabel = computed(() => {
        const counts = this.statusCounts();
        const concluded = counts[ObligationStatus.Paid] + counts[ObligationStatus.Expired];
        return concluded === 0 ? '—' : `${this.stats()?.collectionRate ?? 0}%`;
    });

    readonly statuses: ObligationStatus[] = [
        ObligationStatus.Open,
        ObligationStatus.HeldForReview,
        ObligationStatus.Paid,
        ObligationStatus.Expired,
        ObligationStatus.Waived,
        ObligationStatus.Cancelled
    ];

    readonly pagination = computed<JJamesPaginationConfig>(() => ({
        currentPage: this.pageIndex(),
        totalPages: Math.max(1, Math.ceil(this.totalCount() / this.pageSize())),
        pageSize: this.pageSize(),
        totalItems: this.totalCount(),
        itemsPerPage: this.pageSize(),
        pageSizeOptions: [10, 20, 50, 100]
    }));

    readonly expandedRowIds = signal<Set<string>>(new Set<string>());

    toggleRowExpand(id?: string): void {
        if (!id) return;
        const current = new Set(this.expandedRowIds());
        if (current.has(id)) {
            current.delete(id);
        } else {
            current.add(id);
        }
        this.expandedRowIds.set(current);
    }

    isRowExpanded(id?: string): boolean {
        return !!id && this.expandedRowIds().has(id);
    }

    copyToClipboard(text?: string, label?: string): void {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            this.snackBar.open(`${label || 'ID'} copied to clipboard`, 'OK', { duration: 2500 });
        });
    }

    /**
     * Only a live obligation can be acted on. The server enforces this too — a second cancel is a
     * BadRequest — but a button that is going to be refused should not look available.
     */
    isLive(obligation: PaymentObligationDto): boolean {
        return obligation.status === ObligationStatus.Open
            || obligation.status === ObligationStatus.HeldForReview;
    }

    ngOnInit(): void {
        this.loadSites();
        this.load();
        this.loadStats();
    }

    /**
     * The sites the selector offers.
     *
     * A failure leaves the selector empty rather than falling back to a hardcoded list: an empty
     * selector means "no narrowing", which is what the page already shows, while a wrong list would
     * invite an operator to filter by a site that is not theirs and read the empty result as "no
     * revenue here".
     */
    private loadSites(): void {
        this.parkingSpaceService
            .parkingSpaceFilterPost(
                undefined, undefined, undefined, undefined, undefined, undefined,
                0, 100, 'Name'
            )
            .subscribe({
                next: (result) => this.sites.set(result?.items ?? []),
                error: (err) => {
                    console.error('Failed to load the parking spaces for the site filter', err);
                    this.sites.set([]);
                }
            });
    }

    /** Narrowing to one site changes both the table and the totals, so both are reloaded. */
    onSiteChange(parkingSpaceId: string): void {
        this.siteFilter.set(parkingSpaceId);
        this.pageIndex.set(0);
        this.load();
        this.loadStats();
    }

    /**
     * Deliberately not tied to the table's filters — but it does follow the site selector.
     *
     * The tiles are the whole picture; the filters are a drill-down into the list. Passing the status
     * filter through would make "Total Paid" read zero whenever an operator looked at, say, waived
     * rows only — a total that changes with the row filter is not a total. The site selector is a
     * different kind of thing: it changes which estate the question is about, so the totals must move
     * with it or they would describe sites the table below is not showing.
     */
    private loadStats(): void {
        const siteId = this.siteFilter() || undefined;

        this.obligationService.paymentObligationsStatsGet(siteId).subscribe({
            next: (result) => {
                this.stats.set(result);
                this.statsFailed.set(false);
            },
            error: (err) => {
                console.error('Failed to load receivables statistics', err);
                this.stats.set(undefined);
                this.statsFailed.set(true);
            }
        });

        this.paymentService.paymentsStatsGet(undefined, undefined, undefined, siteId).subscribe({
            next: (result) => {
                this.revenueStats.set(result);
            },
            error: (err) => {
                console.error('Failed to load direct revenue statistics', err);
                this.revenueStats.set(undefined);
            }
        });
    }

    setTab(tab: ObligationsTab): void {
        this.activeTab.set(tab);
    }

    load(): void {
        this.isLoading.set(true);
        const status = this.statusFilter();
        // The plate, not the stay id: an operator has the plate in front of them and never a guid.
        // Separators are stripped server-side, so "W-1234 X" finds the same stays as "W1234X".
        const plate = this.plateFilter().trim();

        this.obligationService
            .paymentObligationsEnrichedGet(
                undefined,
                plate || undefined,
                status || undefined,
                undefined,
                undefined,
                this.pageIndex(),
                this.pageSize(),
                'CreatedAt',
                SortOrder.Descending,
                // Narrows inside the caller's own grants; it can never widen them, and a site they
                // hold no grant on comes back empty rather than populated.
                this.siteFilter() || undefined
            )
            .subscribe({
                next: (result) => {
                    this.obligations.set(result?.items ?? []);
                    this.totalCount.set(result?.totalCount ?? 0);
                    this.isLoading.set(false);
                },
                error: (err) => {
                    console.error('Failed to load payment obligations', err);
                    this.obligations.set([]);
                    this.totalCount.set(0);
                    this.isLoading.set(false);
                    this.snackBar.open('Could not load payment obligations.', 'Dismiss', { duration: 5000 });
                }
            });
    }

    applyFilters(): void {
        this.pageIndex.set(0);
        this.load();
    }

    clearFilters(): void {
        this.statusFilter.set('');
        this.plateFilter.set('');
        this.applyFilters();
    }

    onPageChange(event: { pageIndex: number; pageSize: number }): void {
        this.pageIndex.set(event.pageIndex);
        this.pageSize.set(event.pageSize);
        this.load();
    }

    act(obligation: PaymentObligationDto, action: ObligationAction): void {
        const data: ObligationActionDialogData = { obligation, action };
        const dialogRef = this.dialog.open(ObligationActionDialogComponent, {
            width: '480px',
            maxWidth: '95vw',
            data
        });

        dialogRef.afterClosed().subscribe((reason: string | undefined) => {
            if (!reason) return;
            this.submit(obligation, action, reason);
        });
    }

    /**
     * Exports to CSV, after asking which rows are meant.
     *
     * The dialog is skipped when the page already holds everything that matches the filters, because
     * then the two answers are the same file and the question is noise.
     */
    /**
     * Stay length as "2 h 15 min", from the server's .NET TimeSpan ("d.hh:mm:ss").
     *
     * Null stays null: an open stay has no duration yet, and rendering "0 min" would claim it does.
     */
    duration(obligation: EnrichedPaymentObligationDto): string | null {
        const raw = obligation.stayDuration;
        if (!raw) return null;

        const match = /^(?:(\d+)\.)?(\d{2}):(\d{2}):(\d{2})/.exec(raw);
        if (!match) return null;

        const days = Number(match[1] ?? 0);
        const hours = days * 24 + Number(match[2]);
        const minutes = Number(match[3]);

        return hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`;
    }

    async exportCsv(): Promise<void> {
        if (this.isExporting()) return;

        const viewCount = this.obligations().length;
        if (viewCount === 0) {
            this.snackBar.open(this.translate.instant('obligations.exportEmpty'), 'Dismiss', { duration: 4000 });
            return;
        }

        if (viewCount >= this.totalCount()) {
            await this.runExport('view');
            return;
        }

        const dialogRef = this.dialog.open(ExportScopeDialogComponent, {
            width: '520px',
            maxWidth: '95vw',
            data: {
                viewCount,
                totalCount: this.totalCount(),
                isFiltered: this.statusFilter() !== ''
                    || this.plateFilter().trim().length > 0
                    || this.siteFilter() !== '',
                maxRows: PaymentObligationsComponent.MaxExportRows
            } satisfies ExportScopeDialogData
        });

        const scope = await firstValueFrom(dialogRef.afterClosed()) as ExportScope | undefined;
        if (!scope) return;

        await this.runExport(scope);
    }

    private async runExport(scope: ExportScope): Promise<void> {
        this.isExporting.set(true);
        try {
            const rows = scope === 'view' ? this.obligations() : await this.fetchAllForExport();
            if (rows.length === 0) {
                this.snackBar.open(this.translate.instant('obligations.exportEmpty'), 'Dismiss', { duration: 4000 });
                return;
            }

            this.download(this.toCsv(rows), scope);

            // Always states the row count, and states the total too when the ceiling cut the export
            // short. A truncated file that reports success is the failure this wording exists to avoid.
            //
            // Truncation is judged by whether the cap was reached, not by comparing against
            // `totalCount()`: that total is from the last list load and a row cancelled meanwhile would
            // otherwise raise a truncation warning about a file that is in fact complete.
            const truncated = rows.length >= PaymentObligationsComponent.MaxExportRows;
            this.snackBar.open(
                truncated
                    ? this.translate.instant('obligations.exportTruncated', {
                        exported: rows.length, total: this.totalCount()
                    })
                    : this.translate.instant('obligations.exportDone', { count: rows.length }),
                'Close',
                { duration: truncated ? 9000 : 4000 }
            );
        } catch (err) {
            console.error('Failed to export payment obligations', err);
            this.snackBar.open(this.translate.instant('obligations.exportFailed'), 'Dismiss', { duration: 6000 });
        } finally {
            this.isExporting.set(false);
        }
    }

    /**
     * Walks every page of the current filter, in bounded chunks.
     *
     * Chunked rather than one big request: the list endpoint applies `PageSize` to the query without a
     * ceiling, so asking for "all of it" in one call hands the server an unbounded read. The row limit
     * caps the download itself — an export nobody can open is not an export — and the caller reports
     * when it bites rather than shipping a short file quietly.
     */
    private async fetchAllForExport(): Promise<EnrichedPaymentObligationDto[]> {
        const status = this.statusFilter();
        const plate = this.plateFilter().trim();
        const collected: EnrichedPaymentObligationDto[] = [];

        for (let page = 0; collected.length < PaymentObligationsComponent.MaxExportRows; page++) {
            const result = await firstValueFrom(this.obligationService.paymentObligationsEnrichedGet(
                undefined,
                plate || undefined,
                status || undefined,
                undefined,
                undefined,
                page,
                PaymentObligationsComponent.ExportPageSize,
                'CreatedAt',
                SortOrder.Descending,
                this.siteFilter() || undefined
            ));

            const items = result?.items ?? [];
            collected.push(...items);

            // Stop on a short page as well as an empty one: without it, a total that shrinks mid-export
            // (a row cancelled while we page) would loop until the row cap.
            if (items.length < PaymentObligationsComponent.ExportPageSize) break;
        }

        return collected.slice(0, PaymentObligationsComponent.MaxExportRows);
    }

    private toCsv(rows: EnrichedPaymentObligationDto[]): string {
        const headers = [
            'Created At', 'Licence Plate', 'Parking Space', 'Entry', 'Exit',
            'Amount', 'Currency', 'Status', 'Net Paid', 'Paid At', 'Payment Method',
            'Reason', 'Parking Stay ID', 'Obligation ID'
        ];

        // Quote everything and double any embedded quote. Reasons are operator free text, so they carry
        // commas, quotes and newlines; an unquoted one silently shifts every later column.
        const quote = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;

        const lines = rows.map(o => [
            quote(o.createdAt),
            quote(o.licensePlate),
            quote(o.parkingSpaceName),
            quote(o.startedAt),
            quote(o.endedAt),
            // Point decimal, unquoted, so spreadsheets read it as a number rather than as text.
            (o.amount ?? 0).toFixed(2),
            quote(o.currency || 'EUR'),
            quote(o.status),
            // Blank, not 0.00, when nothing was ever paid — "paid nothing" and "no payment record"
            // are different answers and a spreadsheet cannot tell them apart from a zero.
            o.netPaidAmount == null ? '' : o.netPaidAmount.toFixed(2),
            quote(o.paidAt),
            quote(o.paymentProviderKind),
            quote(o.reason),
            quote(o.parkingProcessId),
            quote(o.id)
        ].join(','));

        // CRLF for spreadsheet tools that still expect it.
        return [headers.join(','), ...lines].join('\r\n');
    }

    private download(csv: string, scope: ExportScope): void {
        // Leading BOM: without it Excel reads the file as the system codepage and mangles every umlaut
        // in an operator's reason text.
        const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        // The scope is in the filename so two exports of the same day are told apart after download.
        link.setAttribute(
            'download',
            `payment-obligations-${scope}-${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    private submit(obligation: PaymentObligationDto, action: ObligationAction, reason: string): void {
        const id = obligation.id!;
        const call =
            action === 'waive' ? this.obligationService.paymentObligationsObligationIdWaivePost(id, { reason })
                : action === 'cancel' ? this.obligationService.paymentObligationsObligationIdCancelPost(id, { reason })
                    : this.obligationService.paymentObligationsObligationIdReissuePost(id, { reason });

        this.isLoading.set(true);
        call.subscribe({
            next: (result) => {
                this.load();
                // All three actions move money between the tiles, so the totals are now stale too.
                this.loadStats();
                this.snackBar.open(this.outcomeMessage(action, result), 'Close', { duration: 6000 });
            },
            error: (err) => {
                this.isLoading.set(false);
                console.error(`Failed to ${action} obligation`, err);
                this.snackBar.open(
                    err?.error?.detail ?? `Could not ${action} the obligation.`,
                    'Dismiss',
                    { duration: 6000 }
                );
            }
        });
    }

    /**
     * Says what actually happened, not what was requested.
     *
     * Recalculating normally cancels the old bill and creates a replacement — but when the new price
     * comes to zero the server cancels **without** a replacement, because a bill for nothing is not a
     * bill. That is a success as far as the API is concerned, so reporting "recalculated" left the
     * operator looking at a cancelled row with no new one and concluding the recalculation had
     * cancelled the parker's payment. The row is right; the message was wrong.
     */
    private outcomeMessage(action: ObligationAction, result: PaymentObligationDto | undefined): string {
        if (action === 'waive') return this.translate.instant('obligations.waived');
        if (action === 'cancel') return this.translate.instant('obligations.cancelled');

        return result?.status === ObligationStatus.Cancelled
            ? this.translate.instant('obligations.reissuedToZero')
            : this.translate.instant('obligations.reissued', {
                amount: (result?.amount ?? 0).toFixed(2),
                currency: result?.currency ?? 'EUR'
            });
    }
}
